import { AfterViewChecked, Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {
  DatatableActionButton,
  DatatableEvent,
  DatatableHandler,
  DatatableHandlerEvents,
  DatatableHandlerSettings,
  IconType,
} from '../datatable/datatable/datatable.component';
import { StudentExamDataService } from '../../../services/data-services/student-exam-data.service';
import { DataTableRoutes } from 'src/app/constants/datatableUrls';
import { ExamReportsClient, FileResponse, StudentResultsClient } from '../../../generated-client/generated-api-client';
import { ToastrService } from 'ngx-toastr';
import { ErrorProcessorService } from '../../../services/error-processor.service';
import { Title } from '@angular/platform-browser';
import { TenantService } from '../../../services/tenant.service';
import { StudentChangesModalComponent } from '../../modals/student-changes-modal/student-changes-modal.component';
import { SimpleModalService } from 'ngx-simple-modal';
import { StudentEventsModalComponent } from '../../modals/student-events-modal/student-events-modal.component';
import { ConfirmModalComponent } from '../../modals/confirm-modal/confirm-modal.component';
import { DataTableDataService } from '../../../services/data-services/navigation-data-services';
import { PersistentDataService } from '../../../services/persistent-data.service';
import { Tooltip } from 'bootstrap';
import { delay, filter, first, map } from 'rxjs/operators';
import { AuthHelperService } from '../../../services/auth-helper.service';
import { RoleConstants } from '../../../constants/roleConstants';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-student-results-datatable',
  templateUrl: './student-results-datatable.component.html',
  styleUrls: ['./student-results-datatable.component.sass'],
})
export class StudentResultsDatatableComponent implements AfterViewChecked, OnDestroy {
  readonly EXPORT_BUTTON_CONFIG = {
    CLASS: 'result-export-button',
    TOOLTIP: {
      IN_PROGRESS: 'Exportálás folyamatban',
      DISABLED_ONLY_CLOSED: 'Az exportálás csak lezárt jegyzőkönyv esetén érhető el.',
      DISABLED_NO_RESULT: 'Az exportálás jelenleg nem elérhető, mert a vizsgajegyzőkönyv nem tartalmaz vizsgázót.',
    },
    TEXT: 'Eredmények exportálása',
  };
  exportButtonElement: HTMLElement | null = null;
  exportButtonWrapper: HTMLElement | null = null;
  exportButtonTooltip: Tooltip | null = null;
  dtHandler?: DatatableHandler;
  dtSettings: DatatableHandlerSettings = DatatableHandler.getDefaultSettings();
  dtEvents: DatatableHandlerEvents = DatatableHandler.getDefaultEvents();
  dtDataSourceUrl: string = DataTableRoutes.StudentList;
  examReportId?: string;
  subs: Subscription = new Subscription();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private _studentExamDataService: StudentExamDataService,
    private _studentResultsClient: StudentResultsClient,
    private _toastr: ToastrService,
    private errorProcessorService: ErrorProcessorService,
    private titleService: Title,
    private _tenantService: TenantService,
    private _modalService: SimpleModalService,
    private _dataTableService: DataTableDataService,
    private _pDservice: PersistentDataService,
    private _examReportClient: ExamReportsClient,
    private authService: AuthHelperService
  ) {
    this.titleService.setTitle('Eredmények');

    route.parent?.paramMap?.subscribe(params => {
      this.examReportId = params.get('id') ?? '';
      this._studentExamDataService.examReportId.next(this.examReportId);
    });

    this.dtSettings.actions = this._dtActionButtonsFn;
    this.dtSettings.pageSize = this._pDservice.examResultsPageSize;
    this.dtSettings.searchString = this._pDservice.examResultsSearchString;

    this.dtSettings.buttons = [];

    if (this.examReportId) {
      this.dtSettings.buttons.push(
        {
          text: 'Eredmények szinkronizálása',
          class: 'btn-primary text-white',
          action: () => {
            this._studentResultsClient.syncResults(this.examReportId!).subscribe(
              response => {
                this.dtHandler?.refresh();
                this._toastr.success('Sikeres szinkronizálás');
              },
              error => this.errorProcessorService.processError(error, true)
            );
          },
        },
        {
          text: 'Eredmények küldése',
          class: 'btn-primary text-white',
          action: () => {
            this.sendAllResultEmails(this.examReportId);
          },
        }
      );
      if (this.userHasExportRole()) {
        this.dtSettings.buttons.push({
          text: this.EXPORT_BUTTON_CONFIG.TEXT,
          class: `btn-primary text-white ${this.EXPORT_BUTTON_CONFIG.CLASS}`,
          action: () => {
            this.exportTable();
          },
          enabled: false,
        });
      }
    }

    this.dtDataSourceUrl = `${DataTableRoutes.StudentList}?ExamReportId=${this.examReportId}`;

    this.dtEvents.selectedRow = (row, data) => {
      this.studentName = data['fullName'];
      this.registrationNumber = data['registrationNumber'];

      this.router.navigate([`${row.id}`], {
        relativeTo: this.route,
      });
    };

    this.dtEvents.unselectedRow = () => {
      const url = this.router.url.split('/');
      this.router.navigate(['..', url[url.length - 2]], {
        relativeTo: this.route,
      });
    };

    this.dtSettings.additionalRequestData = () => {
      if (this.dtHandler?.pageSize != null) _pDservice.examResultsPageSize = this.dtHandler?.pageSize;
      if (this.dtHandler?.searchString != null) _pDservice.examResultsSearchString = this.dtHandler?.searchString;
      return {
        tenantId: this._tenantService.activeTenantId,
      };
    };

    this._tenantService.tenantId.subscribe(tenantId => {
      this.dtHandler?.refresh();
    });
  }

  ngAfterViewChecked() {
    if (this.userHasExportRole() && !this.exportButtonElement) {
      this.exportButtonElement = this.dtHandler?.getButton(`.${this.EXPORT_BUTTON_CONFIG.CLASS}`)?.nodes()[0] ?? null;
      if (this.exportButtonElement) {
        // disabled gombra nem lehet tooltipet rakni, ezért kell egy wrapper span köré
        if (this.exportButtonElement.parentElement?.tagName !== 'SPAN') {
          const wrapper = document.createElement('span');
          this.exportButtonElement.parentNode?.insertBefore(wrapper, this.exportButtonElement);
          wrapper.style.display = 'inline-block';
          wrapper.appendChild(this.exportButtonElement);
        }
        this.exportButtonWrapper = this.exportButtonElement.parentElement;
      }
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  private _dtActionButtonsFn: (data: any) => DatatableActionButton[] = data => {
    const changesAction = new DatatableActionButton(
      'student_changes',
      'Vizsgázó adat eltérések',
      'priority_high',
      undefined,
      IconType.Danger
    );

    const eventsAction = new DatatableActionButton(
      'events',
      'Vizsgázót érintő események',
      'event',
      undefined,
      IconType.Normal
    );

    return data['hasChanges'] ? [eventsAction, changesAction] : [eventsAction];
  };

  private exportTable(): void {
    if (this.userHasExportRole() && this.exportButton) {
      this.exportButton.disable();
      this.exportButton.text(
        `<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> ${this.EXPORT_BUTTON_CONFIG.TEXT}`
      );
      this.updateExportTooltip(this.EXPORT_BUTTON_CONFIG.TOOLTIP.IN_PROGRESS, true);

      const resetExportButtonText = () => {
        this.exportButton?.enable();
        this.exportButton?.text(this.EXPORT_BUTTON_CONFIG.TEXT);
        this.clearExportButtonTooltip();
      };

      this.subs.add(
        this._studentResultsClient
          .downloadResultsXlsx(this.examReportId)
          .pipe(first(), delay(500))
          .subscribe({
            next: response => {
              this.downloadFile(response);
              this._toastr.success('Sikeres exportálás');
              resetExportButtonText();
            },
            error: () => {
              this._toastr.error('Hiba az exportálás során');
              resetExportButtonText();
            },
          })
      );
    }
  }

  private downloadFile({ data, fileName }: FileResponse) {
    const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName ?? '';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  private updateExportButtonState() {
    // A használt table komponens nem frissül be időben (amire az angular lifecycle hook-ok lefutnak)
    setTimeout(async () => {
      const [isTableEmpty, isExamReportClosed] = await Promise.all([
        this.handleEmptyTable(),
        this.handleNonClosedExamReport(),
      ]);
      const tooltip = isTableEmpty
        ? this.EXPORT_BUTTON_CONFIG.TOOLTIP.DISABLED_NO_RESULT
        : !isExamReportClosed
        ? this.EXPORT_BUTTON_CONFIG.TOOLTIP.DISABLED_ONLY_CLOSED
        : '';
      if (tooltip) {
        this.exportButton?.disable();
        this.updateExportTooltip(tooltip);
      } else {
        this.exportButton?.enable();
        this.clearExportButtonTooltip();
      }
    });
  }

  private handleEmptyTable(): Promise<boolean> {
    return new Promise(resolve => {
      const tableInstance = this.dtHandler?.tableInstance();
      const isTableEmpty = tableInstance?.rows().count() === 0;
      resolve(isTableEmpty ?? true);
    });
  }

  private handleNonClosedExamReport(): Promise<boolean> {
    return this._examReportClient
      .get(this.examReportId || '')
      .pipe(
        first(),
        map(({ realExamEndedAtUtc }) => !!realExamEndedAtUtc)
      )
      .toPromise();
  }

  private updateExportTooltip(message: string, forceShow = false) {
    if (this.exportButtonWrapper) {
      this.clearExportButtonTooltip();
      this.exportButtonWrapper.setAttribute('data-bs-original-title', message);
      this.exportButtonTooltip = new Tooltip(this.exportButtonWrapper);
      if (forceShow) {
        this.exportButtonTooltip.show();
      }
    }
  }

  private clearExportButtonTooltip() {
    this.exportButtonTooltip?.dispose();
    this.exportButtonTooltip = null;
  }

  private userHasExportRole(): boolean {
    return this.authService.hasRole([RoleConstants.Admin, RoleConstants.TenantAdmin, RoleConstants.Clerk]);
  }

  private sendAllResultEmails(examReportId: string | undefined): void {
    this._modalService
      .addModal(ConfirmModalComponent, {
        title: 'Összes eredmény értesítő email küldése',
        message: 'Biztosan ki akarja értesíteni a jegyzőkönyvön lévő összes tanulót az eredményéről?',
        confirmText: 'Igen',
        confirmClass: 'btn-primary text-white',
      })
      .subscribe(result => {
        if (result) {
          this._studentResultsClient.sendAllResultEmails(examReportId).subscribe(
            () => {
              this._toastr.success('Sikeres email küldés');
              this.resultDataTable?.refresh(() => {});
            },
            error => this._toastr.error('Sikertelen email küldés')
          );
        }
      });
  }

  get exportButton() {
    return this.dtHandler?.getButton(`.${this.EXPORT_BUTTON_CONFIG.CLASS}`);
  }

  get resultDataTable() {
    return this._dataTableService.dataTable;
  }

  set registrationNumber(value: string) {
    this._studentExamDataService.registrationNumber.next(value);
  }

  set studentName(value: string) {
    this._studentExamDataService.studentName.next(value);
  }

  onDtInit(dtHandler: DatatableHandler) {
    this.dtHandler = dtHandler;

    if (this.route.firstChild) {
      this.route.firstChild.paramMap.subscribe(next => {
        const studentId = next.get('id');
        if (studentId) {
          this.dtEvents.initComplete = () => {
            const row = this.dtHandler?.getRowById(studentId);
            row?.querySelector('td')?.click();
          };
        }

        this.dtHandler?.create();
      });
    } else {
      this.dtHandler.create();
    }

    if (this.userHasExportRole()) {
      this.dtHandler?.tableInstance()?.on('xhr', event => {
        this.updateExportButtonState();
      });

      this.subs.add(
        this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
          const url = this.router.url;
          const regex = /^\/exam-reports\/[^\/]+\/results\/[^\/]+$/;
          if (regex.test(url)) {
            this.updateExportButtonState();
          }
        })
      );
    }
  }

  onAction(event: DatatableEvent) {
    if (event.actionId === 'student_changes') {
      this._modalService.addModal(StudentChangesModalComponent, {
        studentId: event.data['id'],
      });
    } else if (event.actionId === 'events') {
      this._modalService.addModal(StudentEventsModalComponent, {
        studentId: event.data['id'],
      });
    }
  }
}
